/**
 * The lanes' data seam: the minute clock and `useCalendarLaneData`, then
 * `CalendarLanes`. Kept apart from the Tasks screen so the minute tick
 * re-renders this subtree only, not the table and its drawers.
 *
 * The Agenda needs a little more than Day and Week -- the open robot
 * alerts for "Needs a look", the schedule actions (`useScheduleActions`,
 * the phone's), and each schedule's next run -- gathered by
 * `AgendaLanes` only while the Agenda is the view on screen, so Day and
 * Week read exactly what they always read.
 */
import { useContext, useMemo, type ReactNode } from 'react';
import { useScheduleOccurrences } from '../../../api/hooks/useSchedules';
import { AuthContext } from '../../../auth/AuthContext';
import { useActionsOff } from '../../../auth/useRolePreview';
import type { TaskState } from '../../../api/rmf-client';
import type { MaintenanceWindow, ScheduleOccurrence, TaskSchedule } from '../../../api/v1';
import type { SiteTimeZone } from '../../../state/attention/words';
import { horizonDays, nextRunWindow } from './agenda-model';
import { CalendarLanes, type CalendarLanesProps, type LanesNavigation } from './CalendarLanes';
import type { LaneItem } from './lane-items';
import type { EditScheduleRequest, LanePanelProps } from './LanePanel';
import { dateKeyOf, type DateKey, type TimeWindow } from './lane-time';
import { groupMembership } from './phone-model';
import { useCalendarIssues, useScheduleActions } from './useScheduleActions';
import { useCalendarLaneData } from './useCalendarLaneData';
import { useMinuteNow } from './useMinuteNow';
import { useLiftCalendar } from './useLiftCalendar';
import type { BlockTimePreset } from './BlockTimeDialog';

const NO_OCCURRENCES: ScheduleOccurrence[] = [];

export function CalendarLanesContainer(props: {
  siteId: string;
  view: 'day' | 'week' | 'agenda';
  dateKey: DateKey;
  /** The URL names no day: `dateKey` is today, and moves at midnight. */
  followsToday?: boolean;
  timeZone: string;
  timeZoneSource?: SiteTimeZone['source'];
  window: TimeWindow;
  /** The Agenda's range, in days from `dateKey`. */
  agendaDays?: number;
  tasks: readonly TaskState[];
  occurrences: readonly ScheduleOccurrence[];
  schedules: readonly TaskSchedule[];
  maintenance: readonly MaintenanceWindow[];
  robotFilter: string;
  fullDay: boolean;
  /** The screen's `scheduleEditsAllowed` (the Agenda's schedule actions). */
  scheduleEditsAllowed?: boolean;
  onNavigate: (patch: LanesNavigation) => void;
  onOpenTask?: (item: LaneItem) => void;
  onSkipOccurrence?: (item: LaneItem) => void;
  onMoveOccurrence?: (item: LaneItem, newStartMs: number) => void;
  onPauseSeries?: LanePanelProps['onPauseSeries'];
  onEditSchedule?: (req: EditScheduleRequest) => void;
  onCreateAtSlot?: (slot: { start: Date; end: Date }) => void;
  onCreateMaintenance?: (range: { startMs: number; endMs: number; reason?: string }) => void;
  onDeleteMaintenance?: (w: MaintenanceWindow) => void | Promise<boolean>;
  /** May this viewer set lift rules on the calendar. */
  liftRulesAllowed?: boolean;
  blockPreset?: BlockTimePreset | null;
  onNewTask?: () => void;
  renderTaskActions?: (task: TaskState) => ReactNode;
  editGroupsHref?: string;
  savedSchedule?: CalendarLanesProps['savedSchedule'];
}) {
  const nowMs = useMinuteNow();
  const lookOnly = useActionsOff();
  const mySub = useContext(AuthContext)?.user?.id ?? null;
  const data = useCalendarLaneData({
    siteId: props.siteId,
    window: props.window,
    nowMs,
    tasks: props.tasks,
    occurrences: props.occurrences,
    schedules: props.schedules,
    maintenance: props.maintenance,
  });
  const liftCalendar = useLiftCalendar();
  const lanes: CalendarLanesProps = {
    siteId: props.siteId,
    view: props.view,
    dateKey: props.dateKey,
    followsToday: props.followsToday,
    todayKey: dateKeyOf(nowMs, props.timeZone),
    timeZone: props.timeZone,
    nowMs,
    data,
    schedules: props.schedules,
    robotFilter: props.robotFilter,
    fullDay: props.fullDay,
    onNavigate: props.onNavigate,
    onOpenTask: props.onOpenTask,
    onSkipOccurrence: props.onSkipOccurrence,
    onMoveOccurrence: props.onMoveOccurrence,
    onPauseSeries: props.onPauseSeries,
    onEditSchedule: props.onEditSchedule,
    onCreateAtSlot: props.onCreateAtSlot,
    onCreateMaintenance: props.onCreateMaintenance,
    onDeleteMaintenance: props.onDeleteMaintenance,
    liftCalendar: !liftCalendar.on
      ? undefined
      : props.liftRulesAllowed
        ? liftCalendar
        : { windowOf: liftCalendar.windowOf },
    blockPreset: props.blockPreset,
    onNewTask: props.onNewTask,
    renderTaskActions: props.renderTaskActions,
    editGroupsHref: props.editGroupsHref,
    lookOnly,
    mySub,
    savedSchedule: props.savedSchedule,
  };
  if (props.view !== 'agenda') return <CalendarLanes {...lanes} />;
  return (
    <AgendaLanes
      lanes={lanes}
      days={props.agendaDays ?? 2}
      window={props.window}
      occurrences={props.occurrences}
      timeZoneSource={props.timeZoneSource ?? 'site'}
      scheduleEditsAllowed={props.scheduleEditsAllowed === true}
    />
  );
}

function AgendaLanes(props: {
  lanes: CalendarLanesProps;
  days: number;
  window: TimeWindow;
  occurrences: readonly ScheduleOccurrence[];
  timeZoneSource: SiteTimeZone['source'];
  scheduleEditsAllowed: boolean;
}) {
  const { lanes } = props;
  const { data, nowMs, timeZone, todayKey } = lanes;
  const membership = useMemo(
    () => groupMembership(data.items, data.groups, data.robots),
    [data.items, data.groups, data.robots],
  );
  const issues = useCalendarIssues({
    timeZone,
    timeZoneSource: props.timeZoneSource,
    nowMs,
    nameGroup: membership.nameGroup,
  });
  const actions = useScheduleActions(props.scheduleEditsAllowed, timeZone);
  // Next runs come from the range on screen when it covers now; paged
  // away from today, from today and tomorrow (one more read).
  const next = nextRunWindow(props.window, nowMs, todayKey, timeZone);
  const own = useScheduleOccurrences(next.ownFetch ? next.window : null);
  const occurrences = next.ownFetch ? (own.data?.occurrences ?? NO_OCCURRENCES) : props.occurrences;
  const mySub = lanes.mySub ?? null;
  return (
    <CalendarLanes
      {...lanes}
      agenda={{
        days: props.days,
        occurrences,
        horizonDays: horizonDays(next.window, todayKey, timeZone),
        issues,
        actions,
        mySub,
      }}
    />
  );
}
